import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import {
  validateMobileNumber,
  formatMobileNumber,
  sendForgotPasswordOTP,
  verifyForgotPasswordOTP,
  clearStoredOTP,
  validatePassword,
  formatCountdown,
} from '../utils/otpUtils';
import './SignupPage.css';
import './ForgotPasswordPage.css';

const ForgotPasswordPage = () => {
  const navigate = useNavigate();

  const [mobile, setMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [phoneSubmitLoading, setPhoneSubmitLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const [otpData, setOtpData] = useState({
    otp: '',
    isOtpSent: false,
    isOtpVerified: false,
    timer: 0,
    attempts: 0,
    maxAttempts: 3,
  });
  const [phoneVerificationToken, setPhoneVerificationToken] = useState(null);
  const otpSectionRef = useRef(null);

  useEffect(() => {
    if (!otpData.isOtpSent || otpData.isOtpVerified) return;
    const t = window.setTimeout(() => {
      otpSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 80);
    return () => window.clearTimeout(t);
  }, [otpData.isOtpSent, otpData.isOtpVerified]);

  const resetPhoneFlow = useCallback(() => {
    setMobile('');
    setPassword('');
    setConfirmPassword('');
    setPhoneVerificationToken(null);
    setOtpData({
      otp: '',
      isOtpSent: false,
      isOtpVerified: false,
      timer: 0,
      attempts: 0,
      maxAttempts: 3,
    });
    clearStoredOTP();
    setFieldErrors({});
  }, []);

  const handleMobileChange = (e) => {
    const { value } = e.target;
    setMobile(formatMobileNumber(value));
    setPhoneVerificationToken(null);
    setOtpData((prev) => ({
      ...prev,
      otp: '',
      isOtpSent: false,
      isOtpVerified: false,
      timer: 0,
      attempts: 0,
    }));
    clearStoredOTP();
    if (fieldErrors.mobile) setFieldErrors((prev) => ({ ...prev, mobile: '' }));
  };

  const handleOtpChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtpData((prev) => ({ ...prev, otp: digitsOnly }));
    if (fieldErrors.otp) setFieldErrors((prev) => ({ ...prev, otp: '' }));
  };

  const sendOtp = async () => {
    setError('');
    if (!mobile.trim()) {
      setFieldErrors((prev) => ({ ...prev, mobile: 'Mobile number is required' }));
      return;
    }
    if (!validateMobileNumber(mobile)) {
      setFieldErrors((prev) => ({
        ...prev,
        mobile: 'Please enter a valid Pakistani mobile number',
      }));
      return;
    }

    try {
      const result = await sendForgotPasswordOTP(mobile);
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          otp: '',
          isOtpSent: true,
          timer: 60,
          attempts: 0,
        }));
        setPhoneVerificationToken(null);
        setFieldErrors((prev) => ({ ...prev, mobile: '' }));
        const countdown = setInterval(() => {
          setOtpData((prev) => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);
      } else {
        setFieldErrors((prev) => ({ ...prev, mobile: result.message }));
      }
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        mobile: 'Failed to send OTP. Please try again.',
      }));
    }
  };

  const verifyOtp = async () => {
    setError('');
    if (!otpData.otp.trim()) {
      setFieldErrors((prev) => ({ ...prev, otp: 'Please enter the OTP' }));
      return;
    }
    if (otpData.otp.length !== 6) {
      setFieldErrors((prev) => ({ ...prev, otp: 'Please enter a 6-digit OTP' }));
      return;
    }

    try {
      const result = await verifyForgotPasswordOTP(otpData.otp, mobile);
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          isOtpVerified: true,
          attempts: 0,
        }));
        setPhoneVerificationToken(result.phoneVerificationToken || null);
        setFieldErrors((prev) => ({
          ...prev,
          otp: '',
          otpSuccess: 'Mobile number verified successfully! ✓',
        }));
        setTimeout(() => {
          setFieldErrors((prev) => ({ ...prev, otpSuccess: '' }));
        }, 3000);
      } else {
        setOtpData((prev) => ({ ...prev, attempts: prev.attempts + 1 }));
        setPhoneVerificationToken(null);
        setFieldErrors((prev) => ({ ...prev, otp: result.message }));
      }
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        otp: 'OTP verification failed. Please try again.',
      }));
    }
  };

  const resendOtp = async () => {
    setError('');
    if (otpData.attempts >= otpData.maxAttempts) {
      setFieldErrors((prev) => ({
        ...prev,
        mobile: 'Maximum attempts reached. Please try again later.',
      }));
      return;
    }

    try {
      const result = await sendForgotPasswordOTP(mobile);
      if (result.success) {
        setOtpData((prev) => ({
          ...prev,
          timer: 60,
          attempts: prev.attempts + 1,
        }));
        setPhoneVerificationToken(null);
        const countdown = setInterval(() => {
          setOtpData((prev) => {
            if (prev.timer <= 1) {
              clearInterval(countdown);
              return { ...prev, timer: 0 };
            }
            return { ...prev, timer: prev.timer - 1 };
          });
        }, 1000);
      } else {
        setFieldErrors((prev) => ({ ...prev, mobile: result.message }));
      }
    } catch {
      setFieldErrors((prev) => ({
        ...prev,
        mobile: 'Failed to resend OTP. Please try again.',
      }));
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setFieldErrors((prev) => ({
      ...prev,
      password: '',
      confirmPassword: '',
      general: '',
    }));

    if (!otpData.isOtpVerified) {
      setError('Please verify your mobile number with the WhatsApp code first.');
      return;
    }

    if (password !== confirmPassword) {
      setFieldErrors((prev) => ({
        ...prev,
        confirmPassword: 'Passwords do not match',
      }));
      return;
    }

    const pv = validatePassword(password);
    if (!pv.success) {
      setFieldErrors((prev) => ({ ...prev, password: pv.message }));
      return;
    }

    setPhoneSubmitLoading(true);
    try {
      const res = await authService.completeForgotPasswordWithPhoneOtp({
        phone: mobile.trim(),
        password,
        phoneVerificationToken: phoneVerificationToken || undefined,
      });

      if (res.success) {
        setMessage(res.message || 'Password updated successfully. You can sign in now.');
        resetPhoneFlow();
        setTimeout(() => navigate('/login'), 2500);
      } else {
        setError(res.error || 'Could not reset password. Please try again.');
      }
    } catch (err) {
      console.error('Phone reset error:', err);
      setError('Network error. Please try again.');
    } finally {
      setPhoneSubmitLoading(false);
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <div className="forgot-password-header">
          <h2>Forgot Password</h2>
          <p>
            Enter your registered mobile number. We will send a WhatsApp code (same as sign up). After you verify it,
            choose a new password.
          </p>
        </div>

        <form onSubmit={handlePhoneSubmit} className="forgot-password-form forgot-phone-form">
          <div className="form-group">
            <label htmlFor="forgot-mobile">Mobile Number</label>
            <div className="mobile-input-group">
              <input
                type="tel"
                id="forgot-mobile"
                name="mobile"
                value={mobile}
                onChange={handleMobileChange}
                className={`input-field mobile-input ${fieldErrors.mobile ? 'error' : ''} ${otpData.isOtpVerified ? 'verified' : ''}`}
                placeholder="03001234567 or +923001234567"
                disabled={phoneSubmitLoading || otpData.isOtpVerified}
                autoComplete="tel"
              />
              <button
                type="button"
                className={`otp-btn ${otpData.isOtpVerified ? 'verified' : ''}`}
                onClick={otpData.isOtpSent ? resendOtp : sendOtp}
                disabled={
                  phoneSubmitLoading ||
                  !mobile.trim() ||
                  otpData.timer > 0 ||
                  otpData.isOtpVerified
                }
              >
                {otpData.isOtpVerified
                  ? '✓ Verified'
                  : otpData.timer > 0
                    ? formatCountdown(otpData.timer)
                    : otpData.isOtpSent
                      ? 'Resend code'
                      : 'Send WhatsApp code'}
              </button>
            </div>
            {fieldErrors.mobile && (
              <span className="error-message">{fieldErrors.mobile}</span>
            )}
            {fieldErrors.otpSuccess && (
              <span className="success-message">{fieldErrors.otpSuccess}</span>
            )}
          </div>

          {otpData.isOtpSent && !otpData.isOtpVerified && (
            <div
              ref={otpSectionRef}
              className="form-group otp-group forgot-otp-panel"
              role="region"
              aria-label="WhatsApp verification code"
            >
              <p className="forgot-otp-step">Step 2 — Enter the code from WhatsApp</p>
              <label htmlFor="forgot-otp">6-digit WhatsApp code</label>
              <div className="otp-input-group">
                <input
                  type="text"
                  id="forgot-otp"
                  value={otpData.otp}
                  onChange={handleOtpChange}
                  className={`input-field otp-input ${fieldErrors.otp ? 'error' : ''}`}
                  placeholder="Code from WhatsApp"
                  maxLength={6}
                  disabled={phoneSubmitLoading}
                  inputMode="numeric"
                />
                <button
                  type="button"
                  className="verify-btn"
                  onClick={verifyOtp}
                  disabled={
                    phoneSubmitLoading ||
                    !otpData.otp.trim() ||
                    otpData.otp.length !== 6 ||
                    otpData.attempts >= otpData.maxAttempts
                  }
                >
                  Verify
                </button>
              </div>
              {fieldErrors.otp && (
                <span className="error-message">{fieldErrors.otp}</span>
              )}
              <div className="otp-info">
                <small>
                  Code sent on WhatsApp to <strong>{mobile}</strong>. If you do not receive it, open WhatsApp on that
                  number and try Resend after the timer.
                </small>
                <small className="format-hint">Format: 03001234567 or +923001234567</small>
                {otpData.attempts > 0 && (
                  <small className="attempts-info">
                    Attempts: {otpData.attempts}/{otpData.maxAttempts}
                  </small>
                )}
              </div>
            </div>
          )}

          {otpData.isOtpVerified && (
            <>
              <div className="form-group">
                <label htmlFor="forgot-password">New password</label>
                <div className="password-input-container">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="forgot-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (fieldErrors.password) {
                        setFieldErrors((prev) => ({ ...prev, password: '' }));
                      }
                    }}
                    className={`input-field password-field ${fieldErrors.password ? 'error' : ''}`}
                    placeholder="Enter new password"
                    disabled={phoneSubmitLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowPassword((v) => !v)}
                    disabled={phoneSubmitLoading}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <i
                      className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`}
                      aria-hidden
                    />
                  </button>
                </div>
                {fieldErrors.password && (
                  <span className="error-message">{fieldErrors.password}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="forgot-confirm-password">Re-enter password</label>
                <div className="password-input-container">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    id="forgot-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (fieldErrors.confirmPassword) {
                        setFieldErrors((prev) => ({ ...prev, confirmPassword: '' }));
                      }
                    }}
                    className={`input-field password-field ${fieldErrors.confirmPassword ? 'error' : ''}`}
                    placeholder="Confirm new password"
                    disabled={phoneSubmitLoading}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="password-toggle-btn"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    disabled={phoneSubmitLoading}
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    <i
                      className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'} eye-icon`}
                      aria-hidden
                    />
                  </button>
                </div>
                {fieldErrors.confirmPassword && (
                  <span className="error-message">{fieldErrors.confirmPassword}</span>
                )}
              </div>

              <button
                type="submit"
                className={`auth-submit ${phoneSubmitLoading ? 'loading' : ''}`}
                disabled={phoneSubmitLoading || !password.trim() || !confirmPassword.trim()}
              >
                {phoneSubmitLoading ? (
                  <div className="loading-spinner">
                    <div className="spinner" />
                    <span>Updating password...</span>
                  </div>
                ) : (
                  'Update password'
                )}
              </button>
            </>
          )}
        </form>

        {error && (
          <div className="forgot-alert forgot-alert-error" role="alert">
            <span className="error-icon">⚠️</span>
            <span>{error}</span>
          </div>
        )}

        {message && (
          <div className="forgot-alert forgot-alert-success">
            <span className="success-icon">✅</span>
            <span>{message}</span>
          </div>
        )}

        <div className="forgot-password-links">
          <Link to="/login" className="back-to-login">
            ← Back to Login
          </Link>
          <Link to="/signup" className="create-account">
            Don&apos;t have an account? Sign up
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
